import httpClient from "@/api/httpClient";
import authFactory from "./authFactory";
import { AuthResponse, AuthResponseData, AuthVersionEnum } from "../types";
import { versionHeaderKeys } from "../constants";
import AuthService from "@/services/AuthService";
import ConfigService from "@/services/ConfigService";
import { checkIsTokenExpired, checkTokenSimilarity } from "@/utils/tokenUtils";
import { getUserDataFromLocalStorage, safeLocalStorage } from "@/utils";

class authVersionService {
  static authenticate = async (): Promise<AuthResponseData> => {
    const savedUser = getUserDataFromLocalStorage();

    if (savedUser?.isLoggedIn && savedUser?.token && !checkIsTokenExpired(savedUser.token)) {
      this.setPromofyTokenHeader(savedUser.token);
      return savedUser as any;
    }

    // Check if required parameters are present before making the auth call
    if (!authFactory.hasRequiredParams()) {
      throw new Error(
        "AUTH_SERVICE_ERRORS: Authentication skipped: Missing required parameters (session)"
      );
    }

    const authConfig = authFactory.getAuthConfig();

    const { data }: AuthResponse = await httpClient.post(authConfig.endpoint, authConfig.payload);

    if (!data || !data.token || !data.user) {
      throw new Error("AUTH_SERVICE_ERRORS: Authentication failed: No data received");
    }

    this.setPromofyTokenHeader(data.token);
    safeLocalStorage.setItem(
      "user",
      JSON.stringify({
        isLoggedIn: true,
        getCurrentUser: data?.user,
        token: data?.token,
        session: authConfig?.payload?.session,
      })
    );

    return data;
  };

  static authenticateWithToken = async ({
    token,
    handleUnauthorized,
  }: {
    token: string;
    handleUnauthorized: () => void;
  }): Promise<AuthResponseData> => {
    const savedUser = getUserDataFromLocalStorage();

    if (checkIsTokenExpired(token)) {
      handleUnauthorized();
      throw new Error("AUTH_SERVICE_ERRORS: Token expired");
    }

    const isSameUserToken = savedUser?.token
      ? checkTokenSimilarity(savedUser?.token, token)
      : false;

    if (
      isSameUserToken &&
      savedUser?.isLoggedIn &&
      savedUser?.token &&
      !checkIsTokenExpired(savedUser?.token)
    ) {
      this.setPromofyTokenHeader(savedUser.token);
      return savedUser as any;
    }

    const authConfig = authFactory.getAuthConfig();

    const { data }: AuthResponse = await httpClient.post(authConfig.endpoint + "/me", { token });

    if (!data || !data.token || !data.user) {
      throw new Error("AUTH_SERVICE_ERRORS: Authentication failed: No data received");
    }

    this.setPromofyTokenHeader(data.token);
    safeLocalStorage.setItem(
      "user",
      JSON.stringify({
        isLoggedIn: true,
        getCurrentUser: data?.user,
        token: data?.token,
        session: authConfig?.payload?.session,
      })
    );

    return data;
  };

  static clearHeaders = () => {
    const authVersion = authFactory.getAuthVersion();
    switch (authVersion) {
      case AuthVersionEnum.V0:
      case AuthVersionEnum.V1:
        if (httpClient.defaults.headers.common[versionHeaderKeys.default]) {
          httpClient.defaults.headers.common[versionHeaderKeys.default] = "";
        }
        break;
    }
  };

  static getVersionHeaders(promoId?: string) {
    const authVersion = authFactory.getAuthVersion();
    switch (authVersion) {
      case AuthVersionEnum.V0:
        return {
          "promofy-auth-hash": AuthService.getHash(),
          "promofy-auth-ts": AuthService.getTs(),
          "promofy-auth-api-key": AuthService.getApiKey(),
          "promofy-lng": ConfigService.getLng(),
          "promofy-session": ConfigService.getSession(),
          ...(promoId && promoId !== "" && { "promofy-promo-id": promoId }),
        };
      case AuthVersionEnum.V1:
        return {
          "x-api-key": AuthService.getApiKey(),
        };
    }
  }

  static setPromofyTokenHeader(token: string) {
    const version = authFactory.getAuthVersion();

    switch (version) {
      case AuthVersionEnum.V0:
      case AuthVersionEnum.V1:
        httpClient.defaults.headers.common[versionHeaderKeys.default] = `Bearer ${token}`;
        break;
    }
  }
}
export default authVersionService;
